import { until } from '@vueuse/core';
import {
  type _ExtractActionsFromSetupStore,
  type _ExtractGettersFromSetupStore,
  type _ExtractStateFromSetupStore,
  type _Method,
  type DefineSetupStoreOptions,
  defineStore,
  type Store,
} from 'pinia';
import { getCurrentInstance, onMounted, onUnmounted, type Ref } from 'vue';

interface SetupStoreHelpers {
  action: <Fn extends _Method>(fn: Fn) => Fn;
}

type AttachHandler = () => (() => void) | void;

type AttachMethod = () => () => void;

type WithAttach<SS> = SS & { attach: AttachMethod };

export function defineAttachableStore<Id extends string, SS>(
  id: Id,
  storeSetup: (
    onAttach: (attachHandler: AttachHandler) => void,
    helpers: SetupStoreHelpers,
  ) => SS,
  options?: DefineSetupStoreOptions<
    Id,
    _ExtractStateFromSetupStore<WithAttach<SS>>,
    _ExtractGettersFromSetupStore<WithAttach<SS>>,
    _ExtractActionsFromSetupStore<WithAttach<SS>>
  >,
) {
  let attachCount = 0;
  let attachHandler: AttachHandler | undefined;
  let detachHandler: ReturnType<AttachHandler> | undefined;

  let store: Store<
    Id,
    _ExtractStateFromSetupStore<WithAttach<SS>>,
    _ExtractGettersFromSetupStore<WithAttach<SS>>,
    _ExtractActionsFromSetupStore<WithAttach<SS>>
  >;

  function attachStore() {
    if (!attachCount++)
      queueMicrotask(() => {
        if (attachHandler) detachHandler = attachHandler();
      });
  }

  function detachStore() {
    if (!--attachCount)
      queueMicrotask(() => {
        if (detachHandler) {
          detachHandler();
          detachHandler = undefined;
        }
      });
  }

  const attach: AttachMethod = () => {
    attachStore();

    let attached = true;

    return () => {
      if (attached) {
        attached = false;

        detachStore();
      }
    };
  };

  return () => {
    if (!store)
      store = defineStore(
        id,
        (helpers) =>
          ({
            ...storeSetup((newAttachHandler: AttachHandler) => {
              attachHandler = newAttachHandler;
            }, helpers),
            attach,
          }) as WithAttach<SS>,
        options,
      )();

    if (getCurrentInstance()) {
      onMounted(attachStore);
      onUnmounted(detachStore);
    }

    return store;
  };
}

export class BusyError extends Error {
  name = 'BusyError';
}

export const busyAction = <T extends Array<unknown>, U>(
  busy: Ref<boolean>,
  fn: (...args: T) => Promise<U>,
) => {
  return async (...args: T): Promise<U> => {
    if (busy.value) throw new BusyError();
    try {
      busy.value = true;
      return await fn(...args);
    } finally {
      busy.value = false;
    }
  };
};

export const untilAction = <T extends Array<unknown>, U>(
  busy: Ref<boolean>,
  fn: (...args: T) => Promise<U>,
) => {
  return async (...args: T): Promise<U> => {
    await until(busy).toBe(false);
    try {
      busy.value = true;
      return await fn(...args);
    } finally {
      busy.value = false;
    }
  };
};
